import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, Percent, Check, MessageSquare, PlusCircle, MinusCircle, FileText } from 'lucide-react';
import { contractService } from '../../services/contractService';
import { Contract } from '../../types/contract';
import { PageTransition } from '../ui/PageTransition';
import { PageTitle } from '../ui/PageTitle';
import { formatCurrency } from '../../utils/currencyUtils';
import { useContracts } from '../../contexts/ContractContext';

const CreateBudget: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { updateContract } = useContracts();
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [brinquedosPrice, setBrinquedosPrice] = useState<string>('');
  const [recreacaoPrice, setRecreacaoPrice] = useState<string>('');
  const [personagensPrice, setPersonagensPrice] = useState<string>('');
  const [personagensList, setPersonagensList] = useState<string>(''); // Novo estado
  const [customFields, setCustomFields] = useState<Array<{ label: string; value: string; }>>([]);
  const [discountPercentage, setDiscountPercentage] = useState<string>('0');
  const [observacoes, setObservacoes] = useState<string>('');
  const [subtotal, setSubtotal] = useState<string>('');
  const [finalAmount, setFinalAmount] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadContract = async () => {
      if (id) {
        try {
          setLoading(true);
          const fetchedContract = await contractService.getContractById(id);
          if (fetchedContract) {
            setContract(fetchedContract);
            setBrinquedosPrice(fetchedContract.brinquedosPrice || '');
            setRecreacaoPrice(fetchedContract.recreacaoPrice || '');
            setPersonagensPrice(fetchedContract.personagensPrice || '');
            setPersonagensList(fetchedContract.personagensList || ''); // Carregar lista de personagens
            setCustomFields(fetchedContract.customBudgetFields || []);
            setDiscountPercentage(fetchedContract.discountPercentage || '0'); // Carregar desconto
            setObservacoes(fetchedContract.observacoes || ''); // Carregar observações
            
            // Se já houver um valor, preencher o campo de orçamento
            if (fetchedContract.valor && fetchedContract.valor.toUpperCase() !== 'N/A') {
              // O valor total será recalculado automaticamente pelo useEffect
              // baseado nos campos de preço carregados acima
            }
          } else {
            navigate('/admin/contratos');
          }
        } catch (error) {
          console.error('Erro ao carregar contrato para orçamento:', error);
          navigate('/admin/contratos');
        } finally {
          setLoading(false);
        }
      }
    };
    loadContract();
  }, [id, navigate]);

  useEffect(() => {
    const calculateFinalAmount = () => {
      let currentSubtotal = 0;
      currentSubtotal += parseFloat(brinquedosPrice.replace(',', '.')) || 0;
      currentSubtotal += parseFloat(recreacaoPrice.replace(',', '.')) || 0;
      currentSubtotal += parseFloat(personagensPrice.replace(',', '.')) || 0;
      customFields.forEach(field => {
        currentSubtotal += parseFloat(field.value.replace(',', '.')) || 0;
      });
      setSubtotal(currentSubtotal.toFixed(2).replace('.', ','));

      const discount = parseFloat(discountPercentage.replace(',', '.')) || 0;

      if (currentSubtotal > 0) {
        const final = currentSubtotal * (1 - discount / 100);
        setFinalAmount(final.toFixed(2).replace('.', ','));
      } else {
        setFinalAmount('');
      }
    };
    calculateFinalAmount();
  }, [brinquedosPrice, recreacaoPrice, personagensPrice, customFields, discountPercentage]);

  const handleAddCustomField = () => {
    setCustomFields(prev => [...prev, { label: '', value: '' }]);
  };

  const handleRemoveCustomField = (index: number) => {
    setCustomFields(prev => prev.filter((_, i) => i !== index));
  };

  const handleCustomFieldChange = (index: number, field: 'label' | 'value', value: string) => {
    setCustomFields(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value.replace(field === 'value' ? /[^0-9,]/g : '', '') } : item
      )
    );
  };

  const handleSaveBudget = async () => {
    if (!contract || !id) {
      alert('Contrato não carregado.');
      return;
    }
    
    if (!finalAmount || parseFloat(finalAmount.replace(',', '.')) <= 0) {
      alert('Por favor, defina valores para o orçamento antes de salvar.');
      return;
    }

    setIsSaving(true);
    try {
      const updates: Partial<Contract> = {
        valor: finalAmount, // Salva o valor final
        status: 'pendente', // Altera o status para pendente após o orçamento
        brinquedosPrice: brinquedosPrice,
        recreacaoPrice: recreacaoPrice,
        personagensPrice: personagensPrice,
        personagensList: personagensList, // Salvar lista de personagens
        customBudgetFields: customFields,
        discountPercentage: discountPercentage, // Salvar desconto
        observacoes: observacoes, // Salvar observações
      };

      await contractService.updateContract(id, updates);
      
      // Atualizar o contrato no contexto global
      updateContract(id, updates);
      
      // Atualizar o contrato local com os novos valores
      setContract(prevContract => {
        if (prevContract) {
          return {
            ...prevContract,
            ...updates
          };
        }
        return prevContract;
      });
      
      alert('Orçamento salvo com sucesso!');
      navigate(`/admin/contratos/${id}`);
    } catch (error) {
      console.error('Erro ao salvar orçamento:', error);
      alert('Erro ao salvar orçamento. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendWhatsApp = () => {
    if (!contract) {
      alert('Contrato não carregado.');
      return;
    }
    
    if (!finalAmount || parseFloat(finalAmount.replace(',', '.')) <= 0) {
      alert('Por favor, defina valores para o orçamento antes de enviar via WhatsApp.');
      return;
    }

    let message = `Olá ${contract.contratante}! Seu orçamento para o evento de ${contract.nomeAniversariante} é de R$ ${finalAmount}.

*Detalhes do Orçamento:*
`;

    if (brinquedosPrice) message += `Brinquedos: R$ ${brinquedosPrice}
`;
    if (recreacaoPrice) message += `Recreação: R$ ${recreacaoPrice}
`;
    if (personagensPrice) message += `Personagens: R$ ${personagensPrice}
`;
    if (personagensList) message += `  - Personagens: ${personagensList}
`; // Adicionar lista de personagens
    customFields.forEach(field => {
      if (field.label && field.value) message += `${field.label}: R$ ${field.value}
`;
    });
    if (discountPercentage && parseFloat(discountPercentage.replace(',', '.')) > 0) message += `Desconto: ${discountPercentage}%
`;

    message += `
*Subtotal:* R$ ${subtotal}
`;
    message += `*Total:* R$ ${finalAmount} 
`;

    if (observacoes) message += `
*Observações:*
${observacoes}
`;

    message += `
Para visualizar e efetuar o pagamento, acesse: https://playkidseventos.com.br/contrato/visualizar/${contract.id}`;

    const whatsappUrl = `https://wa.me/55${contract.telefone?.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
            <p className="text-slate-600">Carregando contrato...</p>
          </div>
        </div>
      </PageTransition>
    );
  }

  if (!contract) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-red-600">Contrato não encontrado.</p>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="w-full mx-auto px-2 sm:px-4 lg:px-6 overflow-x-hidden">
        {/* Header Principal */}
        <div className="mx-2 sm:mx-4 lg:mx-6 mt-2 mb-3">
          <PageTitle
            title="Montar Orçamento"
            subtitle={`Contrato de ${contract.contratante} para ${contract.nomeAniversariante}`}
          >
            <button
              onClick={() => navigate(`/admin/contratos/${id}`)}
              className="px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 flex items-center gap-2 font-semibold border border-white/30 transition-all duration-200"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao Contrato
            </button>
          </PageTitle>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coluna do Formulário (2/3) */}
          <div className="lg:col-span-2 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 p-4 sm:p-8">
            <div className="space-y-6">
              <h2 className="text-lg sm:text-2xl font-bold text-slate-900 flex items-center gap-3 mb-6">
                <DollarSign className="w-6 h-6 text-teal-600" />
                Definir Valores
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Campo Brinquedos */}
              <div>
                <label htmlFor="brinquedosPrice" className="block text-sm font-medium text-gray-700 mb-2">
                  Brinquedos (R$)
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="brinquedosPrice"
                    value={brinquedosPrice}
                    onChange={(e) => setBrinquedosPrice(e.target.value.replace(/[^0-9,]/g, ''))}
                    className="w-full pl-10 p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 border-slate-300 bg-white/50"
                    placeholder="Ex: 500,00"
                  />
                </div>
              </div>

                {/* Campo Recreação */}
              <div>
                <label htmlFor="recreacaoPrice" className="block text-sm font-medium text-gray-700 mb-2">
                  Recreação (R$)
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="recreacaoPrice"
                    value={recreacaoPrice}
                    onChange={(e) => setRecreacaoPrice(e.target.value.replace(/[^0-9,]/g, ''))}
                    className="w-full pl-10 p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 border-slate-300 bg-white/50"
                    placeholder="Ex: 800,00"
                  />
                </div>
              </div>

                {/* Campo Personagens */}
              <div>
                <label htmlFor="personagensPrice" className="block text-sm font-medium text-gray-700 mb-2">
                  Personagens (R$)
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="personagensPrice"
                    value={personagensPrice}
                    onChange={(e) => setPersonagensPrice(e.target.value.replace(/[^0-9,]/g, ''))}
                    className="w-full pl-10 p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 border-slate-300 bg-white/50"
                    placeholder="Ex: 300,00"
                  />
                </div>
              </div>
                {/* Campo Lista de Personagens */}
              <div>
                <label htmlFor="personagensList" className="block text-sm font-medium text-gray-700 mb-2">
                  Quais Personagens? (Opcional)
                </label>
                <input
                  type="text"
                  id="personagensList"
                  value={personagensList}
                  onChange={(e) => setPersonagensList(e.target.value)}
                  className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 border-slate-300 bg-white/50"
                  placeholder="Ex: Mickey, Minnie, Pateta"
                />
              </div>

                {/* Campo Desconto */}
              <div>
                <label htmlFor="discountPercentage" className="block text-sm font-medium text-gray-700 mb-2">
                  Desconto (%)
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Percent className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="discountPercentage"
                    value={discountPercentage}
                    onChange={(e) => setDiscountPercentage(e.target.value.replace(/[^0-9,]/g, ''))}
                    className="w-full pl-10 p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 border-slate-300 bg-white/50"
                    placeholder="Ex: 10"
                  />
                </div>
              </div>
              </div>

              {/* Campos Personalizados */}
              <div className="space-y-4 mt-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-800">Outros Itens</h3>
                  <button
                    onClick={handleAddCustomField}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors text-sm font-medium"
                  >
                    <PlusCircle className="w-4 h-4" />
                    Adicionar Campo
                  </button>
                </div>

                {customFields.map((field, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div className="md:col-span-1">
                      <label htmlFor={`custom-label-${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                        Descrição
                      </label>
                      <input
                        type="text"
                        id={`custom-label-${index}`}
                        value={field.label}
                        onChange={(e) => handleCustomFieldChange(index, 'label', e.target.value)}
                        className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 border-slate-300 bg-white/50"
                        placeholder="Ex: Taxa de Deslocamento"
                      />
                    </div>
                    <div className="md:col-span-1">
                      <label htmlFor={`custom-value-${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                        Valor (R$)
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <DollarSign className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id={`custom-value-${index}`}
                          value={field.value}
                          onChange={(e) => handleCustomFieldChange(index, 'value', e.target.value)}
                          className="w-full pl-10 p-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 border-slate-300 bg-white/50"
                          placeholder="Ex: 100,00"
                        />
                      </div>
                    </div>
                    <div className="md:col-span-1 flex items-center justify-end">
                      <button
                        onClick={() => handleRemoveCustomField(index)}
                        className="p-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition-colors"
                        title="Remover campo"
                      >
                        <MinusCircle className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Campo Observações */}
              <div className="mt-6">
                <label htmlFor="observacoes" className="block text-sm font-medium text-gray-700 mb-2">
                  Observações (Opcional)
                </label>
                <textarea
                  id="observacoes"
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 border-slate-300 bg-white/50"
                  placeholder="Ex: O cliente pediu para chegar 30 minutos antes."
                />
              </div>

              <div className="mt-8 p-4 bg-teal-50 rounded-xl border border-teal-200 flex items-center justify-between">
                <p className="text-lg font-semibold text-teal-800">Valor Final:</p>
                <p className="text-2xl font-bold text-teal-900">
                {finalAmount && parseFloat(finalAmount.replace(',', '.')) > 0 
                  ? formatCurrency(finalAmount) 
                  : 'Gratuito'}
              </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <button
                  onClick={handleSaveBudget}
                  disabled={isSaving || !finalAmount || parseFloat(finalAmount.replace(',', '.')) <= 0}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 text-lg font-semibold bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      Salvar Orçamento
                    </>
                  )}
                </button>

                <button
                  onClick={handleSendWhatsApp}
                  disabled={!finalAmount || parseFloat(finalAmount.replace(',', '.')) <= 0 || isSaving}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 text-lg font-semibold bg-green-600 text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transform transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MessageSquare className="w-5 h-5" />
                  Enviar via WhatsApp
                </button>
              </div>
            </div>
          </div>

          {/* Coluna da Nota Fiscal (1/3) */}
          <div className="lg:col-span-1 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 p-4 sm:p-6 h-fit sticky top-4">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-purple-600" />
              Resumo do Orçamento
            </h2>

            <div className="space-y-3 text-sm text-slate-700">
              <div className="flex justify-between items-center">
                <span>Brinquedos:</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{formatCurrency(brinquedosPrice || '0')}</span>
                  {brinquedosPrice && parseFloat(brinquedosPrice.replace(',', '.')) > 0 && (
                    <button
                      onClick={() => setBrinquedosPrice('')}
                      className="p-0.5 bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition-colors"
                      title="Remover Brinquedos"
                    >
                      <MinusCircle className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span>Recreação:</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{formatCurrency(recreacaoPrice || '0')}</span>
                  {recreacaoPrice && parseFloat(recreacaoPrice.replace(',', '.')) > 0 && (
                    <button
                      onClick={() => setRecreacaoPrice('')}
                      className="p-0.5 bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition-colors"
                      title="Remover Recreação"
                    >
                      <MinusCircle className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span>Personagens:</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{formatCurrency(personagensPrice || '0')}</span>
                  {personagensPrice && parseFloat(personagensPrice.replace(',', '.')) > 0 && (
                    <button
                      onClick={() => setPersonagensPrice('')}
                      className="p-0.5 bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition-colors"
                      title="Remover Personagens"
                    >
                      <MinusCircle className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              {personagensList && (
                <div className="flex justify-between text-xs text-slate-600 italic items-center">
                  <span>  - Quais:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{personagensList}</span>
                    <button
                      onClick={() => setPersonagensList('')}
                      className="p-0.5 bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition-colors"
                      title="Remover Lista de Personagens"
                    >
                      <MinusCircle className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}

              {customFields.map((field, index) => (
                <div key={index} className="flex justify-between">
                  <span>{field.label || `Item Personalizado ${index + 1}`}:</span>
                  <span className="font-medium">{formatCurrency(field.value || '0')}</span>
                </div>
              ))}

              <div className="border-t border-slate-200 pt-3 mt-3 flex justify-between font-semibold text-slate-800">
                <span>Subtotal:</span>
                <span>{formatCurrency(subtotal || '0')}</span>
              </div>

              <div className="flex justify-between text-red-600 items-center">
                <span>Desconto:</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{discountPercentage}%</span>
                  {discountPercentage !== '0' && (
                    <button
                      onClick={() => setDiscountPercentage('0')}
                      className="p-0.5 bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition-colors"
                      title="Remover Desconto"
                    >
                      <MinusCircle className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              <div className="border-t border-slate-300 pt-3 mt-3 flex justify-between text-lg font-bold text-teal-700">
                <span>Total:</span>
                <span>{formatCurrency(finalAmount || '0')}</span>
              </div>
            </div>

          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default CreateBudget;
